/**
 * Height-aware ground picking (terraced land, PRD #536 Slice C).
 *
 * `screenToGroundTile` has always inverted the dimetric projection against the
 * flat z=0 ground plane. On a heighted map a raised tile draws `z * Z_STEP` px
 * up the screen, so the flat inverse under a click on a hill lands up to
 * `z * Z_STEP / (2 * TILE_HALF)` tiles short of the tile actually painted
 * there - tap-to-walk on a tall terrace would path to the tile *behind* the
 * one clicked.
 *
 * {@link pickGroundTile} resolves the click the way the terraces are drawn: a
 * tile `t` at height `z(t)` contains the screen point iff inverting the
 * projection **at that tile's own z-plane** rounds back to `t`. Candidates all
 * share the flat inverse's (fixed) `x - y` diagonal, shifted along `x + y` by
 * at most the map's height budget, so the search is a small bounded band. When
 * several tiles claim the point (a low tile and the tall terrace drawn over
 * it), the front-most (greatest `x + y`, then highest z) wins - exactly the
 * paint order. A point over no tile's diamond (the open "cliff face" gap
 * between two terrace steps) falls back to the flat-plane inverse, today's
 * answer.
 *
 * Pure and Pixi-free; on a flat map (`maxAbsLandZ` 0) it returns the flat
 * inverse untouched, byte-identical to the old math.
 */
import { TILE_HALF, Z_STEP } from '../sim/projection';

export interface PickedTile {
  x: number;
  y: number;
}

export function pickGroundTile(
  sx: number,
  sy: number,
  landZ: (x: number, y: number) => number,
  maxAbsLandZ: number,
): PickedTile {
  // The dimetric inverse against the plane at height z: y_screen = (x+y)*TILE_HALF - z*Z_STEP.
  const invert = (z: number): PickedTile => ({
    x: (sx + sy + z * Z_STEP) / (2 * TILE_HALF),
    y: (sy + z * Z_STEP - sx) / (2 * TILE_HALF),
  });

  const flat = invert(0);
  if (maxAbsLandZ === 0) return flat;

  // How many whole tiles the tallest/deepest terrain can shift a tile's diamond
  // along the x+y diagonal (the same slack the cull margin uses), plus one for
  // rounding safety.
  const reach = Math.ceil((maxAbsLandZ * Z_STEP) / (2 * TILE_HALF)) + 1;
  const t0 = { x: Math.round(flat.x), y: Math.round(flat.y) };

  let best: PickedTile | undefined;
  let bestKey = -Infinity;
  for (let i = -reach; i <= reach; i++) {
    // Every z-plane inverse shares the flat inverse's fractional x - y, so a
    // candidate's (x - y) can differ from the flat rounding by at most 1.
    for (let j = Math.max(-reach, i - 1); j <= Math.min(reach, i + 1); j++) {
      const cx = t0.x + i;
      const cy = t0.y + j;
      const z = landZ(cx, cy);
      const f = invert(z);
      // Containment: inverting at the candidate's own plane must round back to it.
      if (Math.round(f.x) !== cx || Math.round(f.y) !== cy) continue;
      // Front-most (greatest x+y) wins, then the higher terrace - the paint order.
      const key = (cx + cy) * 1024 + z;
      if (key > bestKey) {
        bestKey = key;
        best = f;
      }
    }
  }
  return best ?? flat;
}
